import React, { useState, useEffect, useContext, useCallback, useRef, useMemo } from 'react';
import {
  CButton, CCard, CCardHeader, CCardBody, CCol, CContainer, CForm, CFormInput, CInputGroup, CInputGroupText, CRow,
  CNav, CNavItem, CNavLink, CBadge, CModal, CModalHeader, CModalBody, CModalFooter, CSpinner,
} from '@coreui/react';
import { AuthUserContext, withEmailVerification } from '../../session';
import { getDatabase, ref, get, update } from 'firebase/database'; // Firebase v9 Realtime Database
import { getAuth, signOut } from 'firebase/auth'; // Firebase v9 Auth
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import UsersService from '../../../services/Users.js';
import FhirService from '../../../services/MiEFhir.js';
import axios from 'axios';
import config from '../../../utils/EnvironmentSwitcher';
import { validateObservation, validateObservationRange, createObservationResource } from '../../../utils/fhirValidation';
import { fetchFhirResourceCount } from '../../../utils/fhirPagination';
import useSubscription from '../../../hooks/useSubscription';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';


// Observation options with LOINC codes where applicable
const observationOptions = [
  { label: 'Calories', unit: 'cal', code: 'cal', loinc: '78950-9', display: 'Calories Intake' },
  { label: 'Weight', unit: 'kg', code: 'kg', loinc: '29463-7', display: 'Body Weight' },
  { label: 'Beers Drank', unit: 'count', code: 'count', loinc: '74013-4', display: 'Alcoholic drinks per day' },
  { label: 'Time on Phone', unit: 'minutes', code: 'min', loinc: null, display: 'Time on Phone' },
  { label: 'Steps Taken', unit: 'steps', code: '{steps}', loinc: '55423-8', display: 'Steps Taken' },
  { label: 'Water Intake', unit: 'liters', code: 'L', loinc: '77299-2', display: 'Water Intake' },
  { label: 'Sleep Hours', unit: 'hours', code: 'h', loinc: '93832-4', display: 'Sleep Duration' },
  { label: 'Heart Rate', unit: 'bpm', code: '/min', loinc: '8867-4', display: 'Heart Rate' },
  { label: 'Blood Pressure Systolic', unit: 'mmHg', code: 'mm[Hg]', loinc: '8480-6', display: 'Systolic Blood Pressure' },
  { label: 'Blood Pressure Diastolic', unit: 'mmHg', code: 'mm[Hg]', loinc: '8462-4', display: 'Diastolic Blood Pressure' },
  { label: 'Body Temperature', unit: '°F', code: '[degF]', loinc: '8310-5', display: 'Body Temperature' },
  { label: 'Oxygen Saturation', unit: '%', code: '%', loinc: '59408-5', display: 'Oxygen Saturation' },
  { label: 'Blood Glucose', unit: 'mg/dL', code: 'mg/dL', loinc: '2339-0', display: 'Blood Glucose' },
  { label: 'Exercise Minutes', unit: 'minutes', code: 'min', loinc: '68516-4', display: 'Exercise Duration' },
  { label: 'Meditation Time', unit: 'minutes', code: 'min', loinc: null, display: 'Meditation Time' },
  { label: 'Screen Time', unit: 'hours', code: 'h', loinc: null, display: 'Screen Time' },
  { label: 'Caffeine Intake', unit: 'mg', code: 'mg', loinc: '91595-9', display: 'Caffeine Intake' },
  { label: 'Sugar Intake', unit: 'g', code: 'g', loinc: null, display: 'Sugar Intake' },
  { label: 'Protein Intake', unit: 'g', code: 'g', loinc: '77489-9', display: 'Protein Intake' },
  { label: 'Carbohydrates Intake', unit: 'g', code: 'g', loinc: null, display: 'Carbohydrate Intake' },
  { label: 'Fat Intake', unit: 'g', code: 'g', loinc: null, display: 'Fat Intake' },
  { label: 'Distance Walked', unit: 'km', code: 'km', loinc: null, display: 'Distance Walked' },
  { label: 'Books Read', unit: 'count', code: 'count', loinc: null, display: 'Books Read' },
  { label: 'Work Hours', unit: 'hours', code: 'h', loinc: null, display: 'Work Hours' },
];

// Demo data for testing - generates realistic sample observations
const generateDemoObservations = () => {
  const now = new Date();
  const observations = [];
  
  // Generate 7 days of demo data
  for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
    const date = new Date(now);
    date.setDate(date.getDate() - dayOffset);
    date.setHours(12, 0, 0, 0);
    
    // Heart Rate - varies between 60-85 bpm
    observations.push({
      option: observationOptions.find(o => o.label === 'Heart Rate'),
      value: 65 + Math.floor(Math.random() * 20),
      date: date.toISOString()
    });
    
    // Body Temperature - around 98.6°F with small variation
    observations.push({
      option: observationOptions.find(o => o.label === 'Body Temperature'),
      value: (97.8 + Math.random() * 1.5).toFixed(1),
      date: date.toISOString()
    });
    
    // Oxygen Saturation - 95-99%
    observations.push({
      option: observationOptions.find(o => o.label === 'Oxygen Saturation'),
      value: 95 + Math.floor(Math.random() * 5),
      date: date.toISOString()
    });
    
    // Blood Glucose - 80-120 mg/dL
    observations.push({
      option: observationOptions.find(o => o.label === 'Blood Glucose'),
      value: 80 + Math.floor(Math.random() * 40),
      date: date.toISOString()
    });
    
    // Steps - 3000-12000 steps
    observations.push({
      option: observationOptions.find(o => o.label === 'Steps Taken'),
      value: 3000 + Math.floor(Math.random() * 9000),
      date: date.toISOString()
    });
    
    // Sleep Hours - 5-9 hours
    observations.push({
      option: observationOptions.find(o => o.label === 'Sleep Hours'),
      value: (5 + Math.random() * 4).toFixed(1),
      date: date.toISOString()
    });
    
    // Weight - consistent around 70kg with small variation
    if (dayOffset === 0) {
      observations.push({
        option: observationOptions.find(o => o.label === 'Weight'),
        value: (68 + Math.random() * 4).toFixed(1),
        date: date.toISOString()
      });
    }
    
    // Calories - 1500-2500 cal
    observations.push({
      option: observationOptions.find(o => o.label === 'Calories'),
      value: 1500 + Math.floor(Math.random() * 1000),
      date: date.toISOString()
    });
    
    // Water Intake - 1.5-3 liters
    observations.push({
      option: observationOptions.find(o => o.label === 'Water Intake'),
      value: (1.5 + Math.random() * 1.5).toFixed(1),
      date: date.toISOString()
    });
    
    // Blood Pressure (only for some days)
    if (dayOffset % 2 === 0) {
      observations.push({
        option: observationOptions.find(o => o.label === 'Blood Pressure Systolic'),
        value: 110 + Math.floor(Math.random() * 20),
        date: date.toISOString()
      });
      observations.push({
        option: observationOptions.find(o => o.label === 'Blood Pressure Diastolic'),
        value: 70 + Math.floor(Math.random() * 15),
        date: date.toISOString()
      });
    }
  }
  
  return observations;
};

// Sample expense categories for everyday life expenses
const expenseCategories = [
  // Groceries & Food
  { code: 'GROCERY', display: 'Grocery Shopping', category: 'Food & Dining', amountRange: [45, 180] },
  { code: 'RESTAURANT', display: 'Restaurant Dining', category: 'Food & Dining', amountRange: [15, 85] },
  { code: 'COFFEE', display: 'Coffee Shop', category: 'Food & Dining', amountRange: [4, 12] },
  { code: 'TAKEOUT', display: 'Takeout / Delivery', category: 'Food & Dining', amountRange: [18, 55] },
  
  // Transportation
  { code: 'GAS', display: 'Gas Station', category: 'Transportation', amountRange: [35, 75] },
  { code: 'PARKING', display: 'Parking', category: 'Transportation', amountRange: [5, 25] },
  { code: 'RIDESHARE', display: 'Uber / Lyft', category: 'Transportation', amountRange: [12, 45] },
  { code: 'TRANSIT', display: 'Public Transit', category: 'Transportation', amountRange: [2.50, 15] },
  
  // Utilities & Bills
  { code: 'ELECTRIC', display: 'Electric Bill', category: 'Utilities', amountRange: [85, 180] },
  { code: 'WATER', display: 'Water Bill', category: 'Utilities', amountRange: [35, 75] },
  { code: 'INTERNET', display: 'Internet Service', category: 'Utilities', amountRange: [60, 120] },
  { code: 'PHONE', display: 'Phone Bill', category: 'Utilities', amountRange: [45, 95] },
  
  // Subscriptions
  { code: 'NETFLIX', display: 'Netflix', category: 'Subscriptions', amountRange: [15.99, 22.99] },
  { code: 'SPOTIFY', display: 'Spotify', category: 'Subscriptions', amountRange: [10.99, 16.99] },
  { code: 'GYM', display: 'Gym Membership', category: 'Subscriptions', amountRange: [25, 75] },
  { code: 'AMAZON', display: 'Amazon Prime', category: 'Subscriptions', amountRange: [14.99, 14.99] },
  { code: 'CLOUD', display: 'Cloud Storage', category: 'Subscriptions', amountRange: [2.99, 9.99] },
  
  // Shopping
  { code: 'CLOTHING', display: 'Clothing Store', category: 'Shopping', amountRange: [25, 150] },
  { code: 'ELECTRONICS', display: 'Electronics', category: 'Shopping', amountRange: [20, 300] },
  { code: 'AMAZON_SHOP', display: 'Amazon Purchase', category: 'Shopping', amountRange: [15, 120] },
  { code: 'HOME_GOODS', display: 'Home Goods', category: 'Shopping', amountRange: [20, 100] },
  
  // Health & Personal Care
  { code: 'PHARMACY', display: 'Pharmacy', category: 'Health', amountRange: [10, 65] },
  { code: 'HAIRCUT', display: 'Haircut / Salon', category: 'Personal Care', amountRange: [20, 75] },
  { code: 'DOCTOR', display: 'Doctor Copay', category: 'Health', amountRange: [25, 50] },
  
  // Entertainment
  { code: 'MOVIES', display: 'Movie Theater', category: 'Entertainment', amountRange: [15, 40] },
  { code: 'GAMING', display: 'Video Games', category: 'Entertainment', amountRange: [10, 70] },
  { code: 'CONCERT', display: 'Concert / Events', category: 'Entertainment', amountRange: [50, 200] },
  
  // Home & Living
  { code: 'RENT', display: 'Rent Payment', category: 'Housing', amountRange: [1200, 2500] },
  { code: 'INSURANCE', display: 'Insurance Premium', category: 'Insurance', amountRange: [100, 250] },
  { code: 'PET', display: 'Pet Supplies', category: 'Pets', amountRange: [25, 80] },
  
  // Miscellaneous
  { code: 'ATM', display: 'ATM Withdrawal', category: 'Cash', amountRange: [40, 200] },
  { code: 'GIFT', display: 'Gift Purchase', category: 'Gifts', amountRange: [25, 100] },
  { code: 'CHARITY', display: 'Charitable Donation', category: 'Donations', amountRange: [10, 50] },
];

// Helper to get random amount within range
const getRandomAmount = (range) => {
  const [min, max] = range;
  return parseFloat((min + Math.random() * (max - min)).toFixed(2));
};

// Generate demo everyday expenses that align with observation dates
const generateDemoCharges = () => {
  const now = new Date();
  const charges = [];
  
  // Helper to add expense with random time
  const addExpense = (dayOffset, code, hour = null) => {
    const date = new Date(now);
    date.setDate(date.getDate() - dayOffset);
    date.setHours(hour || (8 + Math.floor(Math.random() * 12)), Math.floor(Math.random() * 60), 0, 0);
    
    const expense = expenseCategories.find(e => e.code === code);
    if (expense) {
      charges.push({
        expense,
        amount: getRandomAmount(expense.amountRange),
        date: date.toISOString()
      });
    }
  };
  
  // Generate expenses for 7 days
  for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
    // Daily coffee habit
    addExpense(dayOffset, 'COFFEE', 7);
    
    // Most days have some food expense
    if (Math.random() > 0.3) {
      const foodOptions = ['GROCERY', 'RESTAURANT', 'TAKEOUT'];
      addExpense(dayOffset, foodOptions[Math.floor(Math.random() * foodOptions.length)]);
    }
  }
  
  // Day 0 (Today) - Busy day
  addExpense(0, 'GAS', 8);
  addExpense(0, 'AMAZON_SHOP', 14);
  addExpense(0, 'TAKEOUT', 19);
  
  // Day 1 - Grocery and subscriptions
  addExpense(1, 'GROCERY', 11);
  addExpense(1, 'NETFLIX', 0);
  addExpense(1, 'SPOTIFY', 0);
  
  // Day 2 - Transportation and entertainment
  addExpense(2, 'RIDESHARE', 9);
  addExpense(2, 'MOVIES', 19);
  addExpense(2, 'RESTAURANT', 21);
  
  // Day 3 - Bills day
  addExpense(3, 'ELECTRIC', 10);
  addExpense(3, 'INTERNET', 10);
  addExpense(3, 'PHONE', 10);
  addExpense(3, 'PHARMACY', 14);
  
  // Day 4 - Shopping day
  addExpense(4, 'CLOTHING', 13);
  addExpense(4, 'HOME_GOODS', 15);
  addExpense(4, 'PARKING', 12);
  addExpense(4, 'RESTAURANT', 18);
  
  // Day 5 - Self-care and pets
  addExpense(5, 'HAIRCUT', 11);
  addExpense(5, 'GYM', 0);
  addExpense(5, 'PET', 16);
  addExpense(5, 'ATM', 12);
  
  // Day 6 - Weekend activities
  addExpense(6, 'TRANSIT', 10);
  addExpense(6, 'CONCERT', 20);
  addExpense(6, 'GIFT', 14);
  addExpense(6, 'CHARITY', 15);
  addExpense(6, 'GAMING', 16);
  
  // Monthly expenses (if within the 7-day window, simulate 1st of month)
  const today = new Date();
  const dayOfMonth = today.getDate();
  if (dayOfMonth <= 7) {
    // Rent on the 1st
    const rentDay = dayOfMonth - 1;
    if (rentDay >= 0 && rentDay < 7) {
      addExpense(rentDay, 'RENT', 9);
      addExpense(rentDay, 'INSURANCE', 9);
      addExpense(rentDay, 'WATER', 9);
      addExpense(rentDay, 'AMAZON', 0);
      addExpense(rentDay, 'CLOUD', 0);
    }
  }
  
  return charges;
};

// ─── Subscription Plans ───────────────────────────────────────────────────────
const PLANS = [
  {
    tier: 'supporter',
    name: 'Supporter',
    price: '$5/mo',
    priceId: import.meta.env.VITE_STRIPE_SUPPORTER_PRICE_ID,
    features: ['Full AI avatar sessions', 'Unlimited GPT widget', 'Advanced health analytics', 'Priority support'],
  },
  {
    tier: 'creator',
    name: 'Creator',
    price: '$10/mo',
    priceId: import.meta.env.VITE_STRIPE_CREATOR_PRICE_ID,
    features: ['Everything in Supporter', 'Sell on MiEMarket', 'Creator dashboard & payouts', 'Stripe Connect'],
  },
  {
    tier: 'lifetime',
    name: 'Lifetime',
    price: '$50 one-time',
    priceId: import.meta.env.VITE_STRIPE_LIFETIME_PRICE_ID,
    features: ['All Creator features forever', 'No monthly charges', 'Early access to new features'],
  },
];

const TIER_BADGE_COLOR = { free: 'secondary', supporter: 'info', creator: 'primary', lifetime: 'warning' };
const STATUS_BADGE_COLOR = { active: 'success', past_due: 'danger', canceled: 'secondary', trialing: 'info' };
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);
const ELECTRIC_BLUE = 'var(--electric-blue)';
const electricBlueText = { color: ELECTRIC_BLUE };
const electricBlueButton = { color: ELECTRIC_BLUE, borderColor: ELECTRIC_BLUE };

// ─── Subscription Section Component ──────────────────────────────────────────
const SubscriptionSection = () => {
  const { tier, status, currentPeriodEnd, cancelAtPeriodEnd, stripeSubscriptionId, loading } = useSubscription();
  const { getValidToken } = useContext(AuthUserContext);
  const [checkoutClientSecret, setCheckoutClientSecret] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const fetchClientSecret = useCallback(() => Promise.resolve(checkoutClientSecret), [checkoutClientSecret]);
  const checkoutOptions = useMemo(() => ({ fetchClientSecret }), [fetchClientSecret]);
  const isDev = import.meta.env.DEV;
  const handledReturnRef = useRef(false);

  useEffect(() => {
    if (handledReturnRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const returnTier = params.get('tier');
    if (!sessionId) return;
    handledReturnRef.current = true;

    // Clean the URL immediately so a manual refresh doesn't re-trigger this handler.
    const url = new URL(window.location.href);
    url.searchParams.delete('session_id');
    url.searchParams.delete('tier');
    const qs = url.searchParams.toString();
    window.history.replaceState({}, '', `${url.pathname}${qs ? `?${qs}` : ''}`);

    const run = async () => {
      try {
        const token = await getValidToken();
        const res = await fetch(`${config.paymentUrl}/checkout-session-status`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ sessionId }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          toast.warning(data?.error || 'Checkout returned but status check failed.');
        } else if (data.synced) {
          toast.success(`You're now subscribed to the ${data.tier || returnTier} plan!`);
        } else {
          toast.info('Payment received. Subscription activation may take a moment — refresh to check status.');
        }
      } catch (err) {
        if (isDev) console.error('[AdminPage][Subscription] checkout-session-status error', err);
        toast.warning(`Checkout verification error: ${err.message}`);
      }
    };

    run();
  }, []);

  useEffect(() => {
    if (!isDev) return;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const returnTier = params.get('tier');
    if (sessionId || returnTier) {
      console.log('[AdminPage][Subscription] return params detected', {
        sessionId,
        returnTier,
        path: window.location.pathname,
      });
    }
  }, [isDev]);

  const handleUpgrade = async (plan) => {
    if (tier === plan.tier) return;
    setCheckoutLoading(true);
    try {
      const token = await getValidToken();
      if (isDev) {
        console.log('[AdminPage][Subscription] create session request', {
          tier: plan.tier,
          hasPriceId: Boolean(plan.priceId),
          paymentUrl: config.paymentUrl,
          hasToken: Boolean(token),
        });
      }
      const res = await fetch(`${config.paymentUrl}/create-subscription-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ priceId: plan.priceId, tier: plan.tier }),
      });
      const data = await res.json();
      if (isDev) {
        console.log('[AdminPage][Subscription] create session response', {
          status: res.status,
          ok: res.ok,
          hasClientSecret: Boolean(data?.clientSecret),
          error: data?.error || null,
        });
      }
      if (data.clientSecret) {
        setCheckoutClientSecret(data.clientSecret);
      } else {
        toast.error(data.error || 'Failed to start checkout');
      }
    } catch (err) {
      if (isDev) console.error('[AdminPage][Subscription] create session error', err);
      toast.error('Checkout error: ' + err.message);
    }
    setCheckoutLoading(false);
  };

  const handleCancel = async () => {
    if (!stripeSubscriptionId) return;
    setActionLoading(true);
    try {
      const token = await getValidToken();
      const res = await fetch(`${config.paymentUrl}/cancel-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ subscriptionId: stripeSubscriptionId }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Subscription will cancel at end of billing period.');
      } else {
        toast.error(data.error || 'Failed to cancel');
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    }
    setCancelConfirm(false);
    setActionLoading(false);
  };

  const handleReactivate = async () => {
    if (!stripeSubscriptionId) return;
    setActionLoading(true);
    try {
      const token = await getValidToken();
      const res = await fetch(`${config.paymentUrl}/reactivate-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ subscriptionId: stripeSubscriptionId }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Subscription reactivated!');
      } else {
        toast.error(data.error || 'Failed to reactivate');
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    }
    setActionLoading(false);
  };

  if (loading) return <div className="text-center py-4"><CSpinner color="primary" /></div>;

  if (checkoutClientSecret) {
    return (
      <CCard className="mb-4">
        <CCardHeader className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0" style={electricBlueText}>Complete Your Subscription</h5>
          <CButton color="ghost" size="sm" onClick={() => setCheckoutClientSecret(null)} style={electricBlueText}>Back</CButton>
        </CCardHeader>
        <CCardBody>
          <EmbeddedCheckoutProvider stripe={stripePromise} options={checkoutOptions}>
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        </CCardBody>
      </CCard>
    );
  }

  const periodEndDate = currentPeriodEnd ? new Date(currentPeriodEnd).toLocaleDateString() : null;

  return (
    <>
      <CCard className="mb-4">
        <CCardHeader><h5 className="mb-0" style={electricBlueText}>Current Plan</h5></CCardHeader>
        <CCardBody>
          <div className="d-flex align-items-center gap-3 flex-wrap mb-3">
            <CBadge color={TIER_BADGE_COLOR[tier] || 'secondary'} className="fs-6 px-3 py-2 text-capitalize">
              {tier}
            </CBadge>
            <CBadge color={STATUS_BADGE_COLOR[status] || 'secondary'} className="text-capitalize">
              {status?.replace('_', ' ')}
            </CBadge>
            {cancelAtPeriodEnd && periodEndDate && (
              <span className="text-warning">Cancels on {periodEndDate}</span>
            )}
            {!cancelAtPeriodEnd && periodEndDate && tier !== 'free' && tier !== 'lifetime' && (
              <span style={electricBlueText}>Next charge: {periodEndDate}</span>
            )}
          </div>
          <div className="d-flex gap-2 flex-wrap">
            {stripeSubscriptionId && !cancelAtPeriodEnd && (
              <CButton color="danger" variant="outline" size="sm" onClick={() => setCancelConfirm(true)} disabled={actionLoading} style={electricBlueButton}>
                Cancel Subscription
              </CButton>
            )}
            {stripeSubscriptionId && cancelAtPeriodEnd && (
              <CButton color="success" variant="outline" size="sm" onClick={handleReactivate} disabled={actionLoading} style={electricBlueButton}>
                {actionLoading ? <CSpinner size="sm" /> : 'Reactivate'}
              </CButton>
            )}
          </div>
        </CCardBody>
      </CCard>

      <CRow className="mb-4 g-3">
        {PLANS.map((plan) => {
          const isCurrent = tier === plan.tier;
          return (
            <CCol key={plan.tier} md="4">
              <CCard className={`h-100 ${isCurrent ? 'border-info' : ''}`}>
                <CCardHeader>
                  <h5 className="mb-0" style={electricBlueText}>{plan.name}</h5>
                  <div className="fw-bold" style={electricBlueText}>{plan.price}</div>
                </CCardHeader>
                <CCardBody className="d-flex flex-column">
                  <ul className="list-unstyled flex-grow-1 mb-3">
                    {plan.features.map((f, i) => (
                      <li key={i} className="mb-1" style={electricBlueText}><span className="me-2" style={electricBlueText}>✓</span>{f}</li>
                    ))}
                  </ul>
                  <CButton
                    color={isCurrent ? 'secondary' : 'primary'}
                    variant="outline"
                    disabled={isCurrent || checkoutLoading || !plan.priceId}
                    onClick={() => handleUpgrade(plan)}
                    className="w-100"
                    style={electricBlueButton}
                  >
                    {isCurrent ? 'Current Plan' : checkoutLoading ? <CSpinner size="sm" /> : tier === 'free' ? 'Subscribe' : 'Switch Plan'}
                  </CButton>
                  {!plan.priceId && <small className="text-danger mt-1">Price ID not configured</small>}
                </CCardBody>
              </CCard>
            </CCol>
          );
        })}
      </CRow>

      <CModal visible={cancelConfirm} onClose={() => setCancelConfirm(false)}>
        <CModalHeader>Cancel Subscription?</CModalHeader>
        <CModalBody>
          <p>Your subscription will remain active until the end of the current billing period, then it will not renew.</p>
          <p className="text-warning mb-0">You will lose access to premium features after that date.</p>
        </CModalBody>
        <CModalFooter>
          <CButton color="ghost" onClick={() => setCancelConfirm(false)} style={electricBlueText}>Keep Subscription</CButton>
          <CButton color="danger" variant="outline" onClick={handleCancel} disabled={actionLoading} style={electricBlueButton}>
            {actionLoading ? <CSpinner size="sm" /> : 'Yes, Cancel'}
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  );
};

// ─── AdminPage Shell ──────────────────────────────────────────────────────────
const AdminPage = () => {
  const authContext = useContext(AuthUserContext);
  const resolvedAuthUser = authContext?.authUser || authContext;
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    console.debug('[AdminPage] mount/render', {
      hasContext: Boolean(authContext),
      hasResolvedAuthUser: Boolean(resolvedAuthUser),
      uid: resolvedAuthUser?.uid || null,
      path: window.location.pathname,
      search: window.location.search,
    });
  }, [authContext, resolvedAuthUser?.uid]);
  return (
    <div>
      {resolvedAuthUser?.uid && <AdminBase authUser={resolvedAuthUser} />}
    </div>
  );
};

const AdminBase = ({ authUser, fhirToken }) => {
  const mieFToken = localStorage.getItem('mieFToken');
  const [weight, setWeight] = useState('');
  const [calories, setCalories] = useState('');
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState(null);
  const [userFhirData, setUserFhirData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [observationCount, setObservationCount] = useState(0);
  const [walletAddress, setWalletAddress] = useState(null);
  const [showInput, setShowInput] = useState(false);
  const [mieWalletExists, setMieWalletExists] = useState(null);
  const [password, setPassword] = useState('');
  const [walletBalance, setWalletBalance] = useState(null); // New state for balance
  const [walletBalanceError, setWalletBalanceError] = useState(null); // Error message when balance fetch fails (e.g. identity not found)
  const [isRegistering, setIsRegistering] = useState(false); // Loading state for ledger onboarding button
  const usersService = new UsersService();
  const fhirService = new FhirService();
  const [selectedObservation, setSelectedObservation] = useState('');
  const [observationValue, setObservationValue] = useState('');
  const [observationDate, setObservationDate] = useState(new Date().toISOString().split('T')[0]); // New state for date
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [isLoadingDemoData, setIsLoadingDemoData] = useState(false);
  const [demoDataProgress, setDemoDataProgress] = useState({ current: 0, total: 0 });
  const [isLoadingDemoCharges, setIsLoadingDemoCharges] = useState(false);
  const [demoChargeProgress, setDemoChargeProgress] = useState({ current: 0, total: 0 });
  const [chargeCount, setChargeCount] = useState(0);
  
  // MiEROS Transfer States
  const [transferRecipient, setTransferRecipient] = useState('');
  const [transferAmount, setTransferAmount] = useState('');
  const [isTransferring, setIsTransferring] = useState(false);
  const [transferHistory, setTransferHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('subscription');

  const auth = getAuth();

  // Helper to get the Firebase token from auth
  const getFirebaseToken = async () => {
    const auth = getAuth();
    const token = await auth.currentUser.getIdToken();
    return token;
  };

  // Function to check if token is expired
  const isTokenExpired = (token) => {
    if (!token) return true;

    try {
      const tokenData = JSON.parse(atob(token.split('.')[1]));
      const expirationTime = tokenData.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();

      return currentTime >= expirationTime;
    } catch (error) {
      console.error('Error parsing token:', error);
      return true;
    }
  };

  // Function to refresh token
  const refreshToken = async () => {
    try {
      if (!auth.currentUser) {
        throw new Error('No authenticated user found');
      }

      const newToken = await auth.currentUser.getIdToken(true);
      console.log('Token refreshed successfully');
      setIsTokenValid(true);
      return newToken;
    } catch (error) {
      console.error('Error refreshing token:', error);
      handleAuthError(error);
      return null;
    }
  };

  // Function to handle authentication errors
  const handleAuthError = (error) => {
    console.error('Authentication error:', error);

    // Handle specific error cases
    switch (error.code) {
      case 'auth/requires-recent-login':
        toast.error('Please sign in again to continue');
        signOut(auth); // Sign out user and redirect to login
        break;
      case 'auth/id-token-expired':
        toast.error('Your session has expired. Please sign in again');
        signOut(auth);
        break;
      default:
        toast.error('Authentication error. Please try again');
    }
    setIsTokenValid(false);
  };


  // Function to get a valid token
  const getValidToken = async () => {
    if (!auth.currentUser) {
      console.error('No authenticated user');
      return null;
    }

    try {
      const currentToken = await auth.currentUser.getIdToken();

      if (isTokenExpired(currentToken)) {
        console.log('Token is expired, refreshing...');
        return await refreshToken();
      }

      setIsTokenValid(true);
      return currentToken;
    } catch (error) {
      console.error('Error getting valid token:', error);
      handleAuthError(error);
      return null;
    }
  };


  const getWalletAddress = async (userId) => {
    try {
      const token = await getFirebaseToken();
      const response = await fetch(`${config.apiUrl}/api/wallet/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      setWalletAddress(data.walletAddress);
    } catch (error) {
      console.error('Error fetching wallet address:', error);
      setWalletAddress('Error fetching wallet address');
    }
  };


  /**
   * Fetch wallet balance. If the user is not on the ledger yet, calls registerUser and retries once.
   * @param {string} userId - Firebase UID
   * @param {boolean} isRetry - Internal: true when this is the retry after registerUser
   */
  const getWalletBalance = async (userId, isRetry = false) => {
    setWalletBalanceError(null);
    try {
      const token = await getFirebaseToken();
      const response = await fetch(`${config.apiUrl}/api/balance/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();

      if (!response.ok) {
        const msg = (data && (data.message || data.error)) || 'Unknown error';
        // Identity not found / balance not found: backend auto-register may have failed; try explicit registerUser and retry
        const isLedgerRelated = /identity not found|balance not found|not found in the wallet|not on ledger/i.test(msg);
        if (isLedgerRelated && !isRetry) {
          try {
            const regRes = await fetch(`${config.apiUrl}/api/registerUser`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({}),
            });
            const regData = await regRes.json();
            if (regRes.ok || (regData && regData.message && regData.message.includes('already registered'))) {
              return getWalletBalance(userId, true); // Retry balance fetch
            }
          } catch (regErr) {
            console.warn('Auto-register on balance failure:', regErr);
          }
        }
        setWalletBalanceError(msg);
        setWalletBalance(null);
        return;
      }

      setWalletBalance(data.balance);
    } catch (error) {
      console.error('Error fetching wallet balance:', error);
      setWalletBalanceError(error.message || 'Error fetching wallet balance');
      setWalletBalance(null);
    }
  };

  /** Manually trigger ledger onboarding (RegisterUser chaincode). */
  const handleRegisterWallet = async () => {
    if (!authUser?.uid) return;
    setIsRegistering(true);
    setWalletBalanceError(null);
    try {
      const token = await getFirebaseToken();
      const response = await fetch(`${config.apiUrl}/api/registerUser`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });
      const data = await response.json();

      if (response.ok) {
        toast.success(data.message || 'Registered on ledger');
        await getWalletBalance(authUser.uid);
      } else {
        const msg = (data && (data.message || data.error)) || 'Registration failed';
        setWalletBalanceError(msg);
        toast.error(msg);
      }
    } catch (error) {
      console.error('Register wallet error:', error);
      setWalletBalanceError(error.message || 'Registration failed');
      toast.error(error.message || 'Registration failed');
    } finally {
      setIsRegistering(false);
    }
  };

  // Transfer MiEROS to another user
  const handleTransferMieros = async () => {
    // Validate inputs
    if (!transferRecipient.trim()) {
      toast.error('Please enter a recipient email or user ID');
      return;
    }

    const amount = parseFloat(transferAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount greater than 0');
      return;
    }

    if (walletBalance !== null && amount > parseFloat(walletBalance)) {
      toast.error('Insufficient balance for this transfer');
      return;
    }

    // Confirm transfer
    const confirmTransfer = window.confirm(
      `Are you sure you want to transfer ${amount} MiEROS to ${transferRecipient}?`
    );
    if (!confirmTransfer) return;

    setIsTransferring(true);

    try {
      const token = await getFirebaseToken();
      
      const response = await fetch(`${config.apiUrl}/api/transfer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          fromUserId: authUser.uid,
          toUserIdentifier: transferRecipient.trim(), // Can be email or userId
          amount: amount,
          memo: `Transfer from ${authUser.email || authUser.uid}`
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Transfer failed');
      }

      toast.success(`Successfully transferred ${amount} MiEROS to ${transferRecipient}!`);
      
      // Clear form
      setTransferRecipient('');
      setTransferAmount('');
      
      // Refresh balance
      getWalletBalance(authUser.uid);
      
      // Add to local transfer history
      setTransferHistory(prev => [{
        id: Date.now(),
        to: transferRecipient,
        amount: amount,
        date: new Date().toISOString(),
        status: 'completed',
        txId: data.transactionId
      }, ...prev].slice(0, 10)); // Keep last 10 transfers

    } catch (error) {
      console.error('Error transferring MiEROS:', error);
      toast.error(`Transfer failed: ${error.message}`);
    } finally {
      setIsTransferring(false);
    }
  };

  const providerId = authUser?.providerData?.[0]?.providerId || 'web3';


  useEffect(() => {
    const fetchGoogleData = async () => {
      if (authUser && authUser.uid) {
        try {
          const db = getDatabase();
          const userRef = ref(db, `users/${authUser.uid}`);
          const snapshot = await get(userRef);
          const data = snapshot.val();
          const validateToken = async () => {
            if (auth.currentUser) {
              const token = await getValidToken();
              setIsTokenValid(!!token);
            }
          };
          setUserData(data);
          setMieWalletExists(data.mieWalletExists || false);
          validateToken();
          setLoading(false);
        } catch (error) {
          console.error('Error fetching user data:', error);
          setLoading(false);
        }
      }
    };

    const fetchMiEROSData = () => {
      const userDataFromSession = JSON.parse(localStorage.getItem('userSession')) || [];
      if (userDataFromSession) {
        setUserFhirData(userDataFromSession);
      }
      setLoading(false);
    };

    if (providerId !== 'local') {
      if (providerId == 'apple.com') {
        fetchGoogleData();
      } else {
        fetchGoogleData();
      }
    } else {
      fetchMiEROSData();
    }

    if (authUser?.uid) {
      //getWalletAddress(authUser.uid);
      getWalletBalance(authUser.uid); // Fetch the balance when the component loads
    }
  }, [authUser]);

  const fetchFhirData = async () => {
    if (authUser && authUser.uid) {
      try {
        console.log('AuthUser:', authUser);
        // Use config.fhirUrl from EnvironmentSwitcher (reads .env.development / .env.production)
        const fhirBaseUrl = config.fhirUrl;
        // Count-only query to avoid traversing every paginated bundle page.
        const baseParams = `identifier=https://fhir.mieros.cloud/fhir/ids|${authUser.uid}&_summary=count`;
        const fhirUrl = `${fhirBaseUrl}/fhir/Observation?${baseParams}`;
        const validToken = await getValidToken();
        if (!validToken) {
          throw new Error('Could not get valid token');
        }
        const headers = {
          'Content-Type': 'application/fhir+json',
          'Authorization': `Bearer ${validToken}`,
        };

        const response = await axios.get(fhirUrl, { headers });
        const bundle = response.data || {};
        const totalCount = Number.isFinite(bundle.total)
          ? bundle.total
          : (Array.isArray(bundle.entry) ? bundle.entry.length : 0);
        setObservationCount(totalCount);
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const onChange = event => {
    setCalories(event.target.value);
  };

  const handleCreateWallet = async () => {
    if (!password) {
      toast.error('Please enter a password');
      return;
    }

    if (userData) {
      const sanitizedUserData = {
        userID: userData.userID,
        email: userData.email,
        name: userData.name,
        password: password,
        phone: userData.phone,
        address: userData.address,
        paymentID: userData.paymentID,
        userType: userData.userType || 'TRD',
      };

      try {
        const response = await usersService.createNewUser(sanitizedUserData);
        toast.success('MiEROS wallet created successfully!');
        if (response.success) {
          const db = getDatabase();
          const userRef = ref(db, `users/${userData.userID}`);
          await update(userRef, { password: sanitizedUserData.password, mieWalletExists: true });

          const currentUserSession = JSON.parse(localStorage.getItem('userSession')) || {};
          const updatedUserSession = {
            ...currentUserSession,
            password: sanitizedUserData.password,
            mieWalletExists: true,
            mieToken: response.accessToken,
          };
          localStorage.setItem('userSession', JSON.stringify(updatedUserSession));
          setMieWalletExists(true);
        }
      } catch (err) {
        toast.error(`Error: ${err.message}`);
      }
    }
  };

  const onSubmit = async event => {
    event.preventDefault();
    if (authUser && authUser.uid) {
      try {
        const response = await fetch(`${config.fhirUrl}/fhir/Observation`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${mieFToken}`,
          },
          body: JSON.stringify({
            resourceType: 'Observation',
            identifier: [
              {
                system: 'https://fhir.mieros.cloud/fhir/ids',
                value: authUser.uid,
              },
            ],
            code: {
              coding: [
                {
                  system: 'http://loinc.org',
                  code: '78950-9',
                  display: 'Calories Intake',
                },
              ],
              text: 'Calories consumed',
            },
            effectiveDateTime: new Date().toISOString(),
            valueQuantity: {
              value: parseInt(calories),
              unit: 'cal',
              system: 'http://unitsofmeasure.org',
              code: 'cal',
            },
            status: 'final',
          }),
        });
        if (response.ok) {
          toast.success('Calories logged successfully!');
          setCalories('');
          fetchFhirData();
        } else {
          throw new Error('Failed to log calories.');
        }
      } catch (error) {
        toast.error('Error logging calories. Please try again.');
      }
    }
  };

  const handleObservationSubmit = async () => {
    if (!selectedObservation || !observationValue) {
      toast.error('Please select an observation and enter a value');
      return;
    }

    const selectedOption = observationOptions.find(option => option.label === selectedObservation);
    const validToken = await getValidToken();
    
    if (!validToken) {
      toast.error('Could not get valid authentication token');
      return;
    }

    // Validate value range before submission
    const rangeValidation = validateObservationRange(selectedOption.label, parseFloat(observationValue));
    if (!rangeValidation.isValid) {
      toast.warning(rangeValidation.message);
      // Continue anyway - it's just a warning
    }

    try {
      toast.info('Submitting observation...');

      // Create date in local timezone
      const localDate = new Date(observationDate);
      // Set time to noon to avoid timezone issues
      localDate.setHours(12, 0, 0, 0);

      // Build observation with LOINC code if available using validation helper
      const observationResource = createObservationResource({
        userId: authUser.uid,
        observationType: selectedOption.label,
        value: observationValue,
        unit: selectedOption.unit,
        unitCode: selectedOption.code,
        loincCode: selectedOption.loinc,
        displayName: selectedOption.display,
        effectiveDateTime: localDate.toISOString(),
        status: 'final'
      });

      // Validate the observation resource
      const validation = validateObservation(observationResource);
      if (!validation.isValid) {
        const errorMsg = validation.errors.map(e => e.message).join('; ');
        toast.error(`Validation failed: ${errorMsg}`);
        console.error('FHIR validation errors:', validation.errors);
        return;
      }

      // Log any warnings
      if (validation.warnings.length > 0) {
        console.warn('FHIR validation warnings:', validation.warnings);
      }

      const response = await fetch(`${config.fhirUrl}/fhir/Observation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/fhir+json',
          'Authorization': `Bearer ${validToken}`,
        },
        body: JSON.stringify(observationResource),
      });

      if (response.ok) {
        toast.success('Observation submitted successfully!');
        setSelectedObservation('');
        setObservationValue('');
        setObservationDate(new Date().toISOString().split('T')[0]); // Reset date to today
        fetchFhirData(); // Refresh the count
      } else {
        const errorText = await response.text();
        console.error('FHIR submission error:', errorText);
        throw new Error('Failed to submit observation.');
      }
    } catch (error) {
      console.error('Error submitting observation:', error);
      toast.error('Error submitting observation. Please try again.');
    }
  };

  const selectedUnit = observationOptions.find(option => option.label === selectedObservation)?.unit || '';

  // Function to load demo/test data
  const handleLoadDemoData = async () => {
    if (!authUser || !authUser.uid) {
      toast.error('Please sign in first');
      return;
    }

    const validToken = await getValidToken();
    if (!validToken) {
      toast.error('Could not get valid authentication token');
      return;
    }

    setIsLoadingDemoData(true);
    const demoObservations = generateDemoObservations();
    setDemoDataProgress({ current: 0, total: demoObservations.length });

    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < demoObservations.length; i++) {
      const obs = demoObservations[i];
      
      try {
        // Build the observation resource
        const observationResource = {
          resourceType: 'Observation',
          status: 'final',
          identifier: [
            {
              system: 'https://fhir.mieros.cloud/fhir/ids',
              value: authUser.uid,
            },
          ],
          category: [
            {
              coding: [
                {
                  system: 'http://terminology.hl7.org/CodeSystem/observation-category',
                  code: 'vital-signs',
                  display: 'Vital Signs'
                }
              ]
            }
          ],
          code: obs.option.loinc ? {
            coding: [
              {
                system: 'http://loinc.org',
                code: obs.option.loinc,
                display: obs.option.display
              }
            ],
            text: obs.option.label
          } : {
            text: obs.option.label
          },
          effectiveDateTime: obs.date,
          valueQuantity: {
            value: parseFloat(obs.value),
            unit: obs.option.unit,
            system: 'http://unitsofmeasure.org',
            code: obs.option.code
          }
        };

        const response = await fetch(`${config.fhirUrl}/fhir/Observation`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/fhir+json',
            'Authorization': `Bearer ${validToken}`,
          },
          body: JSON.stringify(observationResource),
        });

        if (response.ok) {
          successCount++;
        } else {
          errorCount++;
          console.error(`Failed to create observation ${obs.option.label}:`, await response.text());
        }
      } catch (error) {
        errorCount++;
        console.error(`Error creating observation ${obs.option.label}:`, error);
      }

      setDemoDataProgress({ current: i + 1, total: demoObservations.length });
    }

    setIsLoadingDemoData(false);
    
    if (errorCount === 0) {
      toast.success(`Successfully loaded ${successCount} demo observations!`);
    } else {
      toast.warning(`Loaded ${successCount} observations, ${errorCount} failed`);
    }
    
    // Refresh the observation count
    fetchFhirData();
  };

  // Function to fetch charge count
  const fetchChargeCount = async () => {
    if (authUser && authUser.uid) {
      try {
        const validToken = await getValidToken();
        if (!validToken) return;
        
        const baseParams = `identifier=https://fhir.mieros.cloud/fhir/ids|${authUser.uid}`;
        const total = await fetchFhirResourceCount(
          `${config.fhirUrl}/fhir/ChargeItem`,
          baseParams,
          {
            'Content-Type': 'application/fhir+json',
            'Authorization': `Bearer ${validToken}`,
          },
          {
            httpClient: (url, opts) => fetch(url, opts).then(async (res) => ({
              ok: res.ok,
              status: res.status,
              json: () => res.json(),
            })),
          }
        );
        setChargeCount(total);
      } catch (error) {
        console.error('Error fetching charge count:', error);
      }
    }
  };

  // Function to load demo charge data
  const handleLoadDemoCharges = async () => {
    if (!authUser || !authUser.uid) {
      toast.error('Please sign in first');
      return;
    }

    const validToken = await getValidToken();
    if (!validToken) {
      toast.error('Could not get valid authentication token');
      return;
    }

    setIsLoadingDemoCharges(true);
    const demoCharges = generateDemoCharges();
    setDemoChargeProgress({ current: 0, total: demoCharges.length });

    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < demoCharges.length; i++) {
      const charge = demoCharges[i];
      
      try {
        // Build the FHIR ChargeItem resource for everyday expense
        const chargeResource = {
          resourceType: 'ChargeItem',
          status: 'billable',
          identifier: [
            {
              system: 'https://fhir.mieros.cloud/fhir/ids',
              value: authUser.uid,
            },
            {
              system: 'https://fhir.mieros.cloud/fhir/expense-ids',
              value: `EXP-${Date.now()}-${i}`,
            }
          ],
          code: {
            coding: [
              {
                system: 'https://fhir.mieros.cloud/expense-categories',
                code: charge.expense.code,
                display: charge.expense.display
              }
            ],
            text: charge.expense.display
          },
          subject: {
            identifier: {
              system: 'https://fhir.mieros.cloud/fhir/ids',
              value: authUser.uid
            },
            display: authUser.email || 'User'
          },
          occurrenceDateTime: charge.date,
          quantity: {
            value: 1
          },
          priceOverride: {
            value: charge.amount,
            currency: 'USD'
          },
          // Store category as extension for easier filtering
          extension: [
            {
              url: 'https://fhir.mieros.cloud/expense-category',
              valueString: charge.expense.category
            }
          ],
          enteredDate: new Date().toISOString()
        };

        const response = await fetch(`${config.fhirUrl}/fhir/ChargeItem`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/fhir+json',
            'Authorization': `Bearer ${validToken}`,
          },
          body: JSON.stringify(chargeResource),
        });

        if (response.ok) {
          successCount++;
        } else {
          errorCount++;
          console.error(`Failed to create expense ${charge.expense.display}:`, await response.text());
        }
      } catch (error) {
        errorCount++;
        console.error(`Error creating expense ${charge.expense.display}:`, error);
      }

      setDemoChargeProgress({ current: i + 1, total: demoCharges.length });
    }

    setIsLoadingDemoCharges(false);
    
    if (errorCount === 0) {
      toast.success(`Successfully loaded ${successCount} demo expenses!`);
    } else {
      toast.warning(`Loaded ${successCount} expenses, ${errorCount} failed`);
    }
    
    // Refresh the expense count
    fetchChargeCount();
  };

  return (
    <div className="app flex-row align-items-center">
      <CContainer>
        {!isTokenValid && (
          <CRow className="justify-content-center mb-3">
            <CCol md="8">
              <CCard className="p-4 border-danger">
                <CCardBody>
                  <h1>Authentication Required</h1>
                  <p>Please sign in again to continue.</p>
                  <CButton color="primary" onClick={() => signOut(auth)}>
                    Sign Out
                  </CButton>
                </CCardBody>
              </CCard>
            </CCol>
          </CRow>
        )}

        {/* Tab navigation */}
        <CNav variant="tabs" role="tablist" className="mb-4">
          <CNavItem>
            <CNavLink
              active={activeTab === 'subscription'}
              onClick={() => setActiveTab('subscription')}
              style={{ cursor: 'pointer', ...electricBlueText }}
            >
              Account &amp; Subscription
            </CNavLink>
          </CNavItem>
          <CNavItem>
            <CNavLink
              active={activeTab === 'wallet'}
              onClick={() => setActiveTab('wallet')}
              style={{ cursor: 'pointer', ...electricBlueText }}
            >
              Wallet
            </CNavLink>
          </CNavItem>
        </CNav>

        {/* ── Tab 1: Account & Subscription ── */}
        {activeTab === 'subscription' && (
          <>
            {loading && <div className="text-center py-3"><CSpinner size="sm" /> Loading...</div>}
            {userData && (
              <CCard className="mb-4">
                <CCardBody className="p-4">
                  <CRow className="align-items-center g-3">
                    <CCol xs="12" md="auto" className="text-center">
                      <div style={{
                        width: 72, height: 72, borderRadius: '50%',
                        background: 'linear-gradient(135deg, #321fdb, #9b2de8)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        margin: '0 auto', fontSize: '1.75rem', color: '#fff', fontWeight: 'bold', flexShrink: 0,
                      }}>
                        {(userData.name || userData.email || 'U')[0].toUpperCase()}
                      </div>
                    </CCol>
                    <CCol xs="12" md>
                      <h4 className="mb-1" style={electricBlueText}>{userData.name || 'User'}</h4>
                      <p className="mb-3" style={electricBlueText}>{userData.email}</p>
                      <CRow className="g-3">
                        <CCol xs="6" lg="3">
                          <small className="d-block mb-1" style={electricBlueText}>Provider</small>
                          <strong className="text-capitalize">{providerId}</strong>
                        </CCol>
                        <CCol xs="6" lg="3">
                          <small className="d-block mb-1" style={electricBlueText}>Phone</small>
                          <strong>{userData.phone || '—'}</strong>
                        </CCol>
                        <CCol xs="6" lg="3">
                          <small className="d-block mb-1" style={electricBlueText}>MiEROS Wallet</small>
                          <CBadge color={userData.mieWalletExists ? 'success' : 'secondary'}>
                            {userData.mieWalletExists ? 'Active' : 'Not set up'}
                          </CBadge>
                        </CCol>
                        <CCol xs="6" lg="3">
                          <small className="d-block mb-1" style={electricBlueText}>MiEROS Balance</small>
                          <strong>{walletBalance !== null ? `${walletBalance} MiEROS` : '—'}</strong>
                        </CCol>
                      </CRow>
                    </CCol>
                  </CRow>
                </CCardBody>
              </CCard>
            )}
            <h4 className="mb-3" style={electricBlueText}>Subscription &amp; Billing</h4>
            <SubscriptionSection />
          </>
        )}
        {/* intentionally empty — old user info row removed; now in Account & Subscription tab */}
        {/* ── Tab 2: Health Data ── */}
        {/* ── Tab 3: Wallet ── */}
        {activeTab === 'wallet' && <>
        <CRow className="justify-content-center">
          <CCol md="8">
            <CCard className="p-4">
              <CCardBody>
                <h4 style={electricBlueText}>Wallet Actions</h4>
                <p className="mb-2" style={electricBlueText}>This button should be enabled if you do not have a MiEROS wallet.</p>
                <div>
                  <CButton
                    onClick={() => setShowInput(!showInput)}
                    disabled={mieWalletExists}
                    variant="outline"
                    style={electricBlueButton}
                  >
                    {showInput ? 'Cancel' : 'Create Wallet'}
                  </CButton>
                  {showInput && (
                    <>
                      <input
                        type="password"
                        placeholder="Enter password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        style={{ marginLeft: '10px' }}
                      />
                      <button onClick={handleCreateWallet} style={{ marginLeft: '10px', background: 'transparent', color: ELECTRIC_BLUE, border: `1px solid ${ELECTRIC_BLUE}` }}>
                        Submit
                      </button>
                    </>
                  )}
                </div>

                {/* New Section for Wallet Address */}
                <div style={{ marginTop: '20px' }}>
                  <CButton onClick={() => getWalletAddress(authUser.uid)} variant="outline" style={electricBlueButton}>Wallet Address</CButton>
                  {walletAddress && <p style={electricBlueText}>Wallet Address: {walletAddress}</p>}
                </div>
                <div style={{ marginTop: '20px' }}>
                  <CButton onClick={() => getWalletBalance(authUser.uid)} variant="outline" style={{ marginTop: '10px', ...electricBlueButton }}>Fetch Wallet Balance</CButton>
                  {walletBalance && <p style={electricBlueText}>Wallet Balance: {walletBalance}</p>}
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>

        {/* MiEROS Transfer Section */}
        <CRow className="justify-content-center">
          <CCol md="8">
            <CCard className="p-4">
              <CCardBody>
                <h4 style={electricBlueText}>💸 Transfer MiEROS</h4>
                <p className="mb-2" style={electricBlueText}>Send MiEROS coins to another user by email or user ID.</p>
                
                {/* Current Balance Display */}
                <div style={{
                  padding: '15px',
                  backgroundColor: 'var(--success-bg)',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  border: '1px solid var(--success-border)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 'bold', color: ELECTRIC_BLUE }}>Your Balance:</span>
                    <span style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--success)' }}>
                      {walletBalance !== null ? `${walletBalance} MiEROS` : walletBalanceError ? '—' : 'Loading...'}
                    </span>
                  </div>
                  <CButton
                    color="link"
                    size="sm"
                    onClick={() => getWalletBalance(authUser.uid)}
                    style={{ padding: '0', marginTop: '5px', ...electricBlueText }}
                  >
                    🔄 Refresh Balance
                  </CButton>
                </div>

                {/* Transfer Form */}
                <CForm>
                  <CInputGroup className="mb-3">
                    <CInputGroupText>📧</CInputGroupText>
                    <CFormInput
                      type="text"
                      placeholder="Recipient Email or User ID"
                      value={transferRecipient}
                      onChange={(e) => setTransferRecipient(e.target.value)}
                      disabled={isTransferring}
                    />
                  </CInputGroup>
                  
                  <CInputGroup className="mb-3">
                    <CInputGroupText>💰</CInputGroupText>
                    <CFormInput
                      type="number"
                      placeholder="Amount to transfer"
                      value={transferAmount}
                      onChange={(e) => setTransferAmount(e.target.value)}
                      min="0.01"
                      step="0.01"
                      disabled={isTransferring}
                    />
                    <CInputGroupText>MiEROS</CInputGroupText>
                  </CInputGroup>

                  <CButton
                    color="success"
                    variant="outline"
                    onClick={handleTransferMieros}
                    disabled={isTransferring || !transferRecipient || !transferAmount}
                    style={{ width: '100%', ...electricBlueButton }}
                  >
                    {isTransferring ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Transferring...
                      </>
                    ) : (
                      <>🚀 Send MiEROS</>
                    )}
                  </CButton>
                  
                  {/* Debug info - remove in production */}
                  <small className="mb-2 d-block mt-2" style={electricBlueText}>
                    Wallet Status: {mieWalletExists ? '✅ Active' : walletBalance !== null ? '✅ Has Balance' : walletBalanceError ? '❌ Setup needed' : '⏳ Checking...'}
                  </small>
                  
                  {walletBalanceError && (
                    <div className="mt-2">
                      <p className="text-warning mb-2" style={{ fontSize: '0.9rem' }}>
                        ⚠️ {walletBalanceError}
                      </p>
                      <CButton color="primary" variant="outline" size="sm" onClick={handleRegisterWallet} disabled={isRegistering} style={electricBlueButton}>
                        {isRegistering ? 'Registering...' : 'Register on ledger'}
                      </CButton>
                    </div>
                  )}
                  {!mieWalletExists && walletBalance === null && !walletBalanceError && (
                    <div className="mt-2">
                      <p className="text-warning mb-2" style={{ fontSize: '0.9rem' }}>
                        ⚠️ Wallet status unknown. Register on the ledger to use MiEROS.
                      </p>
                      <CButton color="primary" variant="outline" size="sm" onClick={handleRegisterWallet} disabled={isRegistering} style={electricBlueButton}>
                        {isRegistering ? 'Registering...' : 'Register on ledger'}
                      </CButton>
                    </div>
                  )}
                </CForm>

                {/* Recent Transfer History */}
                {transferHistory.length > 0 && (
                  <div style={{ marginTop: '20px' }}>
                    <h5 style={electricBlueText}>Recent Transfers</h5>
                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      {transferHistory.map(tx => (
                        <div
                          key={tx.id}
                          style={{
                            padding: '10px',
                            marginBottom: '8px',
                            backgroundColor: 'var(--glass-bg)',
                            border: '1px solid var(--border-subtle)',
                            borderRadius: '6px',
                            borderLeft: tx.status === 'completed' ? '4px solid var(--success)' : '4px solid var(--warning)'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={electricBlueText}>To: <strong style={electricBlueText}>{tx.to}</strong></span>
                            <span style={{ color: 'var(--danger)', fontWeight: 'bold' }}>
                              -{tx.amount} MiEROS
                            </span>
                          </div>
                          <small style={electricBlueText}>
                            {new Date(tx.date).toLocaleString()}
                            {tx.txId && <span style={{ marginLeft: '10px' }}>ID: {tx.txId.substring(0, 12)}...</span>}
                          </small>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
        </>}

        {/* ── Health Data: Test Observations ── */}
        {false && <CRow className="justify-content-center">
          <CCol md="8">
            <CCard className="p-4">
              <CCardBody>
                <h4>Test Observations</h4>
                
                {/* Demo Data Section */}
                <div style={{
                  marginBottom: '20px',
                  padding: '15px',
                  backgroundColor: 'var(--info-bg)',
                  borderRadius: '8px',
                  border: '1px solid var(--info-border)'
                }}>
                  <h5 style={{ marginBottom: '10px', color: 'var(--text-primary)' }}>🧪 Load Demo Data</h5>
                  <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', marginBottom: '10px' }}>
                    Creates 7 days of sample health observations including: Heart Rate, Body Temperature,
                    Oxygen Saturation, Blood Glucose, Steps, Sleep, Weight, Calories, Water Intake, and Blood Pressure.
                  </p>
                  <CButton
                    color="info"
                    onClick={handleLoadDemoData}
                    disabled={isLoadingDemoData || !isTokenValid}
                    style={{ marginRight: '10px' }}
                  >
                    {isLoadingDemoData ? (
                      <>Loading... ({demoDataProgress.current}/{demoDataProgress.total})</>
                    ) : (
                      <>Load Demo Data</>
                    )}
                  </CButton>
                  {isLoadingDemoData && (
                    <div style={{ marginTop: '10px' }}>
                      <div style={{
                        width: '100%',
                        height: '8px',
                        backgroundColor: 'var(--border-default)',
                        borderRadius: '4px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: `${(demoDataProgress.current / demoDataProgress.total) * 100}%`,
                          height: '100%',
                          background: 'linear-gradient(90deg, var(--electric-blue), var(--electric-purple))',
                          transition: 'width 0.2s'
                        }} />
                      </div>
                    </div>
                  )}
                </div>

                {/* Demo Expenses Section */}
                <div style={{
                  marginBottom: '20px',
                  padding: '15px',
                  backgroundColor: 'var(--warning-bg)',
                  borderRadius: '8px',
                  border: '1px solid var(--warning-border)'
                }}>
                  <h5 style={{ marginBottom: '10px', color: 'var(--text-primary)' }}>💳 Load Demo Expenses</h5>
                  <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', marginBottom: '10px' }}>
                    Creates 7 days of realistic everyday expenses including: Groceries, Restaurants, Coffee, Gas, Rideshare,
                    Netflix, Spotify, Gym, Phone Bill, Electric, Internet, Amazon, Clothing, Pharmacy, Haircuts, Movies,
                    Rent, Insurance, and more (~40+ transactions).
                  </p>
                  <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '10px' }}>
                    Current Expense Count: <strong>{chargeCount}</strong>
                    <CButton
                      color="link"
                      size="sm"
                      onClick={fetchChargeCount}
                      style={{ padding: '0 5px' }}
                    >
                      🔄
                    </CButton>
                  </p>
                  <CButton
                    color="warning"
                    onClick={handleLoadDemoCharges}
                    disabled={isLoadingDemoCharges || !isTokenValid}
                    style={{ marginRight: '10px' }}
                  >
                    {isLoadingDemoCharges ? (
                      <>Loading... ({demoChargeProgress.current}/{demoChargeProgress.total})</>
                    ) : (
                      <>Load Demo Expenses</>
                    )}
                  </CButton>
                  {isLoadingDemoCharges && (
                    <div style={{ marginTop: '10px' }}>
                      <div style={{
                        width: '100%',
                        height: '8px',
                        backgroundColor: 'var(--border-default)',
                        borderRadius: '4px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: `${(demoChargeProgress.current / demoChargeProgress.total) * 100}%`,
                          height: '100%',
                          background: 'linear-gradient(90deg, var(--warning), var(--electric-pink))',
                          transition: 'width 0.2s'
                        }} />
                      </div>
                    </div>
                  )}
                </div>

                {/* Manual Observation Entry */}
                <h5 style={{ marginTop: '20px', marginBottom: '10px', color: 'var(--text-primary)' }}>📝 Manual Entry</h5>
                <select
                  className="form-select admin-observation-select"
                  value={selectedObservation}
                  onChange={(e) => setSelectedObservation(e.target.value)}
                >
                  <option value="">Select an observation</option>
                  {observationOptions.map((option, index) => (
                    <option key={index} value={option.label}>
                      {option.label} {option.loinc ? `(LOINC: ${option.loinc})` : ''}
                    </option>
                  ))}
                </select>
                {selectedUnit && <span style={{ marginLeft: '10px', color: 'var(--electric-blue)' }}>Unit: {selectedUnit}</span>}
                <CFormInput
                  type="number"
                  placeholder="Enter value"
                  value={observationValue}
                  onChange={(e) => setObservationValue(e.target.value)}
                  style={{ marginTop: '10px' }}
                />
                <CFormInput
                  type="date"
                  value={observationDate}
                  onChange={(e) => setObservationDate(e.target.value)}
                  style={{ marginTop: '10px' }}
                />
                <CButton
                  color="primary"
                  className="ml-3"
                  onClick={handleObservationSubmit}
                  size="sm"
                  style={{ marginTop: '10px' }}
                >
                  Submit Observation
                </CButton>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>}

      </CContainer>
    </div>
  );
};

export default withEmailVerification(AdminPage);



